import { useCallback, useEffect, useRef } from "react";
import {
  fetchUserRegistrations,
} from "../api/conferenceRegistrationApi";
import { loadExistingPaperState } from "../existingRegistration/existingPaperLoader";
import {
  buildExistingRegistrationFormData,
  buildKnownUserRegistrationSeed,
  buildNewConferenceRegistrationSeed,
  getCategoryHistoryFromRegistration,
  getExistingRegistrationPaymentStatus,
  getStoredFeeCollections,
} from "../existingRegistration/existingRegistrationMapper";
import { getExistingRegistrationNavigationDecision } from "../existingRegistration/existingRegistrationNavigation";
import { getFocusedSessionIdsFromStoredFees } from "../existingRegistration/existingRegistrationSessions";

export const useExistingRegistrationCheck = ({
  existingUser,
  conferenceName,
  registrationBaseUrl,
  conferenceId,
  accompanyingPersonFee,
  setFormData,
  setStep,
  conferenceDataLoadedRef,
  registrationCheckRequestIdRef,
  setHasPaperSubmitted,
  setPaperApprovalStatus,
  setSubmittedPapers,
  setCategoriesHistory,
  setRegistrationTypesHistory,
  setInitialPaidCategoryId,
  setInitialPaidRegistrationTypeId,
  setInitialPaidCategoryValue,
  setInitialPaidRegistrationTypeValue,
  setExistingPaperId,
  setIsPaperLocked,
  setPaperTitle,
  setAbstractText,
  setPresentationMode,
  setSubmissionMethod,
  setSelectedTrack,
  setWantsToSubmitAbstract,
  setPaperFileUploaded,
  setPaperFileName,
  setCoAuthors,
  restoreAccompanyingPersonsFromFees,
  setRegisterForFocusedSessions,
  setSelectedFocusedSessions,
  setFocusedSessionFees,
  fetchFocusedSessionFee,
  navigationDecisionRef,
  setShowPaperSubmissionSummary,
  isPendingPaperApprovalStatus,
  isApprovalMandateEnabled,
  abstractStepNumber,
  setResumePostPaymentAbstract,
  setIsInitialLoad,
  setNavigationDecisionVersion,
}) => {
  const lastAutoCheckKeyRef = useRef(null);
  const checkExistingRegistrationRef = useRef(null);

  const checkExistingRegistration = useCallback(
    async (email, conferenceNameParam) => {
      if (!email) {
        return;
      }

      const requestId = ++registrationCheckRequestIdRef.current;
      const isLatestRequest = () =>
        registrationCheckRequestIdRef.current === requestId;
      const safeSetStep = (nextStep) => {
        if (!isLatestRequest()) {
          console.log(
            "⏭️ Skipping stale step update from older registration check:",
            nextStep
          );
          return;
        }
        setStep(nextStep);
      };

      conferenceDataLoadedRef.current = false;
      setFormData((prev) => ({
        ...prev, id: null, regId: null, paid: false,
        payment: [], additionalFees: [], zeroPayments: [],
        registrationLookupPending: true, registrationLookupError: null,
      }));
      setHasPaperSubmitted(false);
      setPaperApprovalStatus(null);
      setSubmittedPapers([]);
      setResumePostPaymentAbstract?.(false);

      try {
        const registrations = await fetchUserRegistrations({
          registrationBaseUrl,
          email,
          conferenceName: conferenceNameParam,
        });

        if (!isLatestRequest()) return;
        setFormData((prev) => ({ ...prev, registrationLookupPending: false }));
        if (registrations.length > 0) {
          const existingReg = registrations.find(
            (reg) => reg.conference === conferenceNameParam && reg.user?.email === email && !reg.cancelled
          );

          if (existingReg && existingReg.user) {
            const isPaid = getExistingRegistrationPaymentStatus(existingReg);

            setFormData((prev) => {
              conferenceDataLoadedRef.current = true;
              return buildExistingRegistrationFormData({
                currentFormData: prev,
                existingReg,
                isPaid,
              });
            });

            const {
              categoriesHistory,
              registrationTypesHistory,
            } = getCategoryHistoryFromRegistration(existingReg);
            setCategoriesHistory(categoriesHistory);
            setRegistrationTypesHistory(registrationTypesHistory);
            if (isPaid) {
              setInitialPaidCategoryId(existingReg.categoryId || null);
              setInitialPaidRegistrationTypeId(
                existingReg.registrationTypeId || null
              );
              setInitialPaidCategoryValue(existingReg.category || "");
              setInitialPaidRegistrationTypeValue(
                existingReg.registrationType || ""
              );
            } else {
              setInitialPaidCategoryId(null);
              setInitialPaidRegistrationTypeId(null);
              setInitialPaidCategoryValue("");
              setInitialPaidRegistrationTypeValue("");
            }

            let hasPaperSubmitted = false;
            let loadedPaperStatus = null;

            try {
              const paperState = await loadExistingPaperState({
                registrationBaseUrl,
                existingReg,
                email,
                conferenceName: conferenceNameParam,
              });

              if (!isLatestRequest()) return;
              hasPaperSubmitted = paperState.hasPaperSubmitted;
              loadedPaperStatus = paperState.loadedPaperStatus;

              if (paperState.hasPaperSubmitted) {
                setHasPaperSubmitted(true);
                setWantsToSubmitAbstract(true);
                setSubmittedPapers(paperState.papers);
              }

              if (paperState.selectedPaper) {
                const paper = paperState.selectedPaper;
                setExistingPaperId(paper.id);
                setIsPaperLocked(paperState.isPaperLocked);
                setPaperApprovalStatus(paper.status || null);
                setPaperTitle(paper.title || "");
                setAbstractText(paper.abstractText || "");
                setPresentationMode(paper.presentationMode || "");
                setSelectedTrack(paper.trackId || null);

                if (paperState.hasFile) {
                  setPaperFileUploaded(true);
                  setPaperFileName(paperState.fileName);
                }

                setSubmissionMethod(paperState.submissionMethod);

                if (paperState.coAuthors.length > 0) {
                  setCoAuthors(paperState.coAuthors);
                }
              }

              if (paperState.loadedPaperStatus && !paperState.selectedPaper) {
                setPaperApprovalStatus(paperState.loadedPaperStatus);
              }
            } catch (error) {
              console.error("Error loading paper data:", error);
            }

            if (!isLatestRequest()) return;
            const {
              additionalFees: storedAdditionalFees,
              zeroPayments: storedZeroPayments,
            } = getStoredFeeCollections(existingReg);

            if (
              (storedAdditionalFees.length > 0 || storedZeroPayments.length > 0) &&
              accompanyingPersonFee
            ) {
              restoreAccompanyingPersonsFromFees({
                additionalFees: storedAdditionalFees,
                zeroPayments: storedZeroPayments,
                replaceExisting: true,
              });

              const focusedSessionsList = getFocusedSessionIdsFromStoredFees({
                additionalFees: storedAdditionalFees,
                zeroPayments: storedZeroPayments,
                accompanyingPersonFee,
              });

              if (focusedSessionsList.length > 0) {
                setRegisterForFocusedSessions("Yes");
                setSelectedFocusedSessions(focusedSessionsList);

                if (conferenceId) {
                  focusedSessionsList.forEach((sessionId) => {
                    fetchFocusedSessionFee(sessionId).then((feeData) => {
                      if (feeData && isLatestRequest()) {
                        setFocusedSessionFees((prevFees) => ({
                          ...prevFees,
                          [sessionId]: feeData,
                        }));
                      }
                    });
                  });
                }
              }
            }

            navigationDecisionRef.current = {
              isPaid,
              hasPaperSubmitted,
              paperStatus: loadedPaperStatus,
              ready: true,
            };
            setNavigationDecisionVersion?.((version) => version + 1);

            const navigationDecision =
              getExistingRegistrationNavigationDecision({
                isPaid,
                hasPaperSubmitted,
                loadedPaperStatus,
                isApprovalMandateEnabled,
                isPendingPaperApprovalStatus,
                abstractStepNumber,
              });

            if (navigationDecision.resumePostPaymentAbstract) {
              setResumePostPaymentAbstract?.(true);
            }

            if (navigationDecision.type === "paperPendingApproval") {
              navigationDecisionRef.current.ready = false;
              setShowPaperSubmissionSummary(
                navigationDecision.showPaperSubmissionSummary
              );
              safeSetStep(navigationDecision.step);
              setIsInitialLoad?.(false);
              return;
            }

            if (navigationDecision.type === "postPaymentPaper") {
              navigationDecisionRef.current.ready = false;
              setShowPaperSubmissionSummary(
                navigationDecision.showPaperSubmissionSummary
              );
              safeSetStep(navigationDecision.step);
              setIsInitialLoad?.(false);
              return;
            }

            // Let useRegistrationNavigation route after abstract metadata has loaded.
            // Routing here can consume the decision while abstractStepNumber is still 0.
          } else if (registrations.length > 0) {
            const anyRegistration = registrations[0];
            if (anyRegistration && anyRegistration.user) {
              setFormData((prev) =>
                buildKnownUserRegistrationSeed({
                  currentFormData: prev,
                  anyRegistration,
                  conferenceName: conferenceNameParam,
                })
              );
            }

            safeSetStep(5);
            setIsInitialLoad?.(false);
          } else {
            setFormData((prev) =>
              buildNewConferenceRegistrationSeed({
                currentFormData: prev,
                conferenceName: conferenceNameParam,
              })
            );

            safeSetStep(1);
            setIsInitialLoad?.(false);
          }
        } else {
          setFormData((prev) => buildNewConferenceRegistrationSeed({
            currentFormData: prev, conferenceName: conferenceNameParam,
          }));
          safeSetStep(5);
          setIsInitialLoad?.(false);
        }
      } catch (error) {
        if (!isLatestRequest()) return;
        console.error("Error checking existing registration:", error);
        setFormData((prev) => ({
          ...prev, id: null, regId: null, paid: false,
          registrationLookupPending: false, registrationLookupError: error.message,
        }));
        alert(error.message);
        safeSetStep(1);
        setIsInitialLoad?.(false);
      }
    },
    [
      accompanyingPersonFee,
      conferenceDataLoadedRef,
      conferenceId,
      fetchFocusedSessionFee,
      isPendingPaperApprovalStatus,
      isApprovalMandateEnabled,
      abstractStepNumber,
      setResumePostPaymentAbstract,
      navigationDecisionRef,
      registrationBaseUrl,
      registrationCheckRequestIdRef,
      restoreAccompanyingPersonsFromFees,
      setAbstractText,
      setCategoriesHistory,
      setCoAuthors,
      setExistingPaperId,
      setFocusedSessionFees,
      setFormData,
      setHasPaperSubmitted,
      setInitialPaidCategoryId,
      setInitialPaidCategoryValue,
      setInitialPaidRegistrationTypeId,
      setInitialPaidRegistrationTypeValue,
      setIsPaperLocked,
      setIsInitialLoad,
      setNavigationDecisionVersion,
      setPaperApprovalStatus,
      setPaperFileName,
      setPaperFileUploaded,
      setPaperTitle,
      setPresentationMode,
      setSubmissionMethod,
      setRegisterForFocusedSessions,
      setRegistrationTypesHistory,
      setSelectedFocusedSessions,
      setSelectedTrack,
      setSubmittedPapers,
      setShowPaperSubmissionSummary,
      setStep,
      setWantsToSubmitAbstract,
    ]
  );

  useEffect(() => {
    checkExistingRegistrationRef.current = checkExistingRegistration;
  }, [checkExistingRegistration]);

  useEffect(() => {
    const selectedConferenceName = conferenceName || existingUser?.conference;
    if (!existingUser || !existingUser.email || !selectedConferenceName) {
      return;
    }

    const autoCheckKey = `${existingUser.email}::${selectedConferenceName}::${isApprovalMandateEnabled}::${abstractStepNumber}`;
    if (lastAutoCheckKeyRef.current === autoCheckKey) {
      return;
    }

    lastAutoCheckKeyRef.current = autoCheckKey;
    checkExistingRegistrationRef.current?.(
      existingUser.email,
      selectedConferenceName
    );
  }, [
    existingUser?.conference,
    existingUser?.email,
    conferenceName,
    isApprovalMandateEnabled,
    abstractStepNumber,
  ]);

  return { checkExistingRegistration };
};
